const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '')

/** Resolve an image path (e.g. /uploads/...) to a full URL usable in <img src>. */
export function resolveImageUrl(src: string | undefined | null): string {
  if (!src) return ''
  if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://') || src.startsWith('blob:')) return src
  return `${API_ORIGIN}${src.startsWith('/') ? '' : '/'}${src}`
}

export function formatItemName(item: { item_code?: string | null; item_name?: string; name?: string } | null | undefined): string {
  if (!item) return 'Unknown Item'
  const name = item.item_name || item.name || 'Unknown Item'
  const code = item.item_code
  return code ? `${code} - ${name}` : name
}

if (import.meta.env.DEV) {
  console.log('API Base URL:', API_BASE_URL)
}

type MenuResponse = { success: boolean; data?: any[]; error?: string }
type CategoriesResponse = { success: boolean; data?: any[]; error?: string }

class CustomerApiService {
  private baseUrl: string
  /** In-memory cache for menu; cleared when tab/browser closes. */
  private menuCache: MenuResponse | null = null
  /** In-memory cache for categories; cleared when tab/browser closes. */
  private categoriesCache: CategoriesResponse | null = null

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
    // Log in production too for debugging
    console.log('CustomerApiService initialized with baseUrl:', this.baseUrl)
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: boolean; data?: T; error?: string }> {
    try {
      const url = `${this.baseUrl}${endpoint}`
      console.log(`API Request: ${options.method || 'GET'} ${url}`)
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      })

      console.log(`API Response: ${response.status} ${response.statusText}`)

      if (!response.ok) {
        const errorText = await response.text()
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { error: errorText || 'Request failed' }
        }
        console.error(`API Error Response:`, errorData)
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      // Check for network/CORS errors
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        console.error('Network error - check CORS settings and API URL:', this.baseUrl)
      }
      
      return {
        success: false,
        error: errorMessage
      }
    }
  }

  async getMenu(): Promise<MenuResponse> {
    if (this.menuCache !== null) {
      return Promise.resolve(this.menuCache)
    }
    const result = await this.request<any[]>('/customer/menu')
    if (result.success && Array.isArray(result.data)) {
      result.data = result.data.map((item: any) => ({ ...item, image: resolveImageUrl(item.image) }))
    }
    this.menuCache = result
    return result
  }

  async getCategories(): Promise<CategoriesResponse> {
    if (this.categoriesCache !== null) {
      return Promise.resolve(this.categoriesCache)
    }
    const result = await this.request<any[]>('/customer/categories')
    this.categoriesCache = result
    return result
  }

  async getRestaurantInfo() {
    return this.request<any>('/customer/restaurant-info')
  }

  async getAllergens() {
    return this.request<Array<{ id: string; name: string; icon: string | null; sort_order: number }>>('/allergens')
  }

  async createOrder(orderData: {
    items: any[]
    customer_mobile: string
    customer_name?: string
    notes?: string
    table_number?: string
  }) {
    return this.request<{
      order_number: string
      accessToken?: string
    }>('/customer/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    })
  }

  async getMyOrders(accessToken: string) {
    return this.request<any[]>('/customer/my-orders', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
  }

  async verifyOrderAccess(phoneNumber: string, orderNumber: string) {
    return this.request<{
      phoneNumber?: string
      accessToken: string
    }>('/customer/verify-order-access', {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber, order_number: orderNumber }),
    })
  }

  async createReservation(data: {
    customer_name: string
    customer_email?: string
    customer_phone: string
    party_size: number
    reservation_time: string
    notes?: string
  }) {
    return this.request<{ id: string }>('/customer/reservations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
}

export const customerApi = new CustomerApiService()

